import React from "react";
import { media } from "@glitz/core";
import { styled } from "@glitz/react";
import type { IntakeTimelineData } from "../lib/energyCalculator";
import {
  fontSize,
  fontWeight,
  spacing,
  green,
  blue,
  grey,
  darkGrey,
  black,
  white,
  mediumGrey,
  orange,
  red,
} from "../theme";

type Props = {
  data: IntakeTimelineData;
  /** Renderas precis under stats-rutorna — t.ex. cykel-flaskplanerare. */
  topSlot?: React.ReactNode;
};

const IntakePlanTimeline: React.FC<Props> = ({ data, topSlot }) => {
  const { stats, prerace, raceItems, notes } = data;

  return (
    <Wrap>
      <Disclaimer>{notes.intro}</Disclaimer>

      <RacePlanCard aria-label="Sammanfattning av raceplanen">
        <RacePlanKicker>Raceplan i korthet</RacePlanKicker>
        <StatsRow>
          <StatCell>
            <StatLabel>Varaktighet</StatLabel>
            <StatValue>{stats.durationLabel}</StatValue>
          </StatCell>
          <StatCell>
            <StatLabel>Kolhydrater / h</StatLabel>
            <StatValue>{stats.carbsPerHour} g</StatValue>
          </StatCell>
          <StatCell>
            <StatLabel>Totalt under lopp</StatLabel>
            <StatValue>{stats.totalCarbs} g kh</StatValue>
          </StatCell>
          <StatCell>
            <StatLabel>Koffein före start</StatLabel>
            <StatValue>
              {stats.caffeineMgTotal > 0 ? `${stats.caffeineMgTotal} mg` : "—"}
            </StatValue>
          </StatCell>
        </StatsRow>
      </RacePlanCard>

      {topSlot}

      <PreloadNote>{notes.preload}</PreloadNote>

      <Timeline>
        <PhaseLabel>Före start</PhaseLabel>
        {prerace.length === 0 ? (
          <TimelineMuted>Inga före start-produkter i förslaget (t.ex. utan koffein).</TimelineMuted>
        ) : (
          prerace.map((row, i) => (
            <TimelineRow key={`${row.timeLabel}-${i}`}>
              <TimeCol>{row.timeLabel}</TimeCol>
              <PreRaceDotCol>
                <PreRaceDot />
              </PreRaceDotCol>
              <CardCol>
                <ProductRow>
                  {row.image ? (
                    <Thumb src={row.image} alt="" />
                  ) : (
                    <ThumbPlaceholder />
                  )}
                  <ProductText>
                    <ProductTitle>
                      {row.preraceQuantityLabel ?? `${row.units} st`} {row.productName}
                    </ProductTitle>
                    <TagRow>
                      {row.carbsG > 0 && <CarbTag>{row.carbsG} g kh</CarbTag>}
                      {row.isNitrate && <NitrateTag>Nitrat</NitrateTag>}
                      {row.caffeineMg != null && row.caffeineMg > 0 && (
                        <CaffeineTag>{row.caffeineMg} mg koffein</CaffeineTag>
                      )}
                    </TagRow>
                  </ProductText>
                </ProductRow>
              </CardCol>
            </TimelineRow>
          ))
        )}

        <PhaseDivider>
          <PhaseLabelStart>Start</PhaseLabelStart>
        </PhaseDivider>

        {raceItems.map((item, i) => {
          if (item.kind === "triathlon_split_note" || item.kind === "race_note") {
            return (
              <TimelineRow key={`note-${i}`}>
                <TimeCol />
                <DotCol>
                  <Dot />
                </DotCol>
                <CardCol>
                  <NotePara>{item.summary}</NotePara>
                </CardCol>
              </TimelineRow>
            );
          }
          if (item.kind === "triathlon_segment_header") {
            return (
              <SegmentHeader key={`seg-${item.segment}-${i}`}>
                <SegmentBadge data-segment={item.segment}>
                  {item.label}
                </SegmentBadge>
                <SegmentHeaderText>
                  <SegmentHeaderTitle>
                    {item.label} · {item.durationLabel}
                  </SegmentHeaderTitle>
                  <SegmentHeaderMeta>
                    Från start: {item.startTimeLabel} — {item.summary}
                  </SegmentHeaderMeta>
                </SegmentHeaderText>
              </SegmentHeader>
            );
          }
          if (item.kind === "cycling_hour") {
            return (
              <TimelineRow key={`h-${item.hourIndex}`}>
                <TimeCol>{item.timeLabel}</TimeCol>
                <DotCol>
                  <Dot />
                </DotCol>
                <CardCol>
                  <ProductThumbStrip>
                    <ThumbCell>
                      {item.barImage ? (
                        <Thumb src={item.barImage} alt="" />
                      ) : (
                        <ThumbPlaceholder />
                      )}
                      <ThumbLabel>Bar</ThumbLabel>
                    </ThumbCell>
                    <ThumbCell>
                      {item.dryckImage ? (
                        <Thumb src={item.dryckImage} alt="" />
                      ) : (
                        <ThumbPlaceholder />
                      )}
                      <ThumbLabel>Sportdryck</ThumbLabel>
                    </ThumbCell>
                    {item.gelImage ? (
                      <ThumbCell>
                        <Thumb src={item.gelImage} alt="" />
                        <ThumbLabel>Gel</ThumbLabel>
                      </ThumbCell>
                    ) : null}
                  </ProductThumbStrip>
                  <CyclingBlockTitle>Cykel — riktlinje per timme</CyclingBlockTitle>
                  {item.wholeBarCarbsG != null && item.wholeBarCarbsG > 0 && (
                    <TagRow>
                      <CarbTag>{item.wholeBarCarbsG} g kh</CarbTag>
                    </TagRow>
                  )}
                  <NotePara>{item.summary}</NotePara>
                </CardCol>
              </TimelineRow>
            );
          }
          return (
            <TimelineRow key={`gel-${item.minutesFromStart}-${i}`}>
              <TimeCol>{item.timeLabel}</TimeCol>
              <DotCol>
                <Dot />
              </DotCol>
              <CardCol>
                <ProductRow>
                  {item.image ? (
                    <Thumb src={item.image} alt="" />
                  ) : (
                    <ThumbPlaceholder />
                  )}
                  <ProductText>
                    <ProductTitle>Under loppet — gel</ProductTitle>
                    {item.carbsG > 0 && (
                      <TagRow>
                        <CarbTag>{item.carbsG} g kh</CarbTag>
                      </TagRow>
                    )}
                    <NotePara>{item.lineSummary}</NotePara>
                  </ProductText>
                </ProductRow>
              </CardCol>
            </TimelineRow>
          );
        })}

        <TimelineRow>
          <TimeCol />
          <DotCol>
            <GoalDot />
          </DotCol>
          <CardCol>
            <GoalLabel>Mål — totaltid {stats.durationLabel}</GoalLabel>
          </CardCol>
        </TimelineRow>
      </Timeline>

      <AfterGoal>{notes.afterGoal}</AfterGoal>
    </Wrap>
  );
};

export default IntakePlanTimeline;

const Wrap = styled.div({
  marginTop: spacing.s,
});

const Banner = styled.div({
  backgroundColor: black,
  color: white,
  fontSize: fontSize.s,
  fontWeight: fontWeight.heavy,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  padding: { xy: spacing.s },
  textAlign: "center",
  borderRadius: "4px",
  marginBottom: spacing.m,
});

const Disclaimer = styled.p({
  fontSize: fontSize.xs,
  color: darkGrey,
  lineHeight: 1.5,
  marginBottom: spacing.m,
});

/**
 * Sammanhållen raceplans-sammanfattning (ersätter tidigare separata chips).
 * Ett stort kort med tydlig kicker-titel och fyra celler separerade av
 * vertikala dividers på desktop / horisontella på mobil.
 */
const RacePlanCard = styled.section({
  backgroundColor: white,
  border: { xy: { style: "solid", color: black, width: "1px" } },
  borderRadius: "12px",
  overflow: "hidden",
  marginBottom: spacing.m,
});

const RacePlanKicker = styled.div({
  fontSize: fontSize.xxs,
  fontWeight: fontWeight.heavy,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: white,
  backgroundColor: black,
  padding: { x: spacing.m, y: spacing.xs },
});

const StatsRow = styled.div({
  display: "grid",
  gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
  alignItems: "stretch",
  ...media({ maxWidth: "767px" }, {
    gridTemplateColumns: "1fr",
  }),
});

/**
 * Fyra celler separerade av tunna dividers — vertikala på desktop,
 * horisontella på mobil.
 */
const StatCell = styled.div({
  padding: { x: spacing.m, y: spacing.m },
  textAlign: "center",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  gap: spacing.xxs,
  borderLeft: { style: "solid", color: grey, width: "1px" },
  ":first-child": { borderLeft: { style: "none" } },
  ...media({ maxWidth: "767px" }, {
    borderLeft: { style: "none" },
    borderTop: { style: "solid", color: grey, width: "1px" },
    ":first-child": { borderTop: { style: "none" } },
  }),
});

const StatLabel = styled.div({
  fontSize: fontSize.xxs,
  color: darkGrey,
  fontWeight: fontWeight.bold,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
});

const StatValue = styled.div({
  fontSize: fontSize.l,
  fontWeight: fontWeight.heavy,
  color: black,
  lineHeight: 1.15,
});

const PreloadNote = styled.p({
  fontSize: fontSize.xs,
  color: black,
  lineHeight: 1.5,
  marginBottom: spacing.l,
});

const Timeline = styled.div({
  position: "relative",
  paddingLeft: "0",
});

const PhaseLabel = styled.div({
  fontSize: fontSize.xs,
  fontWeight: fontWeight.bold,
  color: darkGrey,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  marginBottom: spacing.s,
});

const PhaseDivider = styled.div({
  display: "flex",
  alignItems: "center",
  gap: spacing.s,
  marginTop: spacing.l,
  marginBottom: spacing.s,
});

const SegmentHeader = styled.div({
  display: "flex",
  alignItems: "flex-start",
  gap: spacing.s,
  marginTop: spacing.l,
  marginBottom: spacing.m,
  padding: { x: spacing.m, y: spacing.s },
  backgroundColor: black,
  color: white,
  borderRadius: "8px",
});

const SegmentBadge = styled.span({
  fontSize: fontSize.xs,
  fontWeight: fontWeight.heavy,
  textTransform: "uppercase",
  letterSpacing: "0.08em",
  padding: { x: spacing.s, y: "4px" },
  borderRadius: "4px",
  backgroundColor: white,
  color: black,
  flexShrink: 0,
});

const SegmentHeaderText = styled.div({
  flexGrow: 1,
  minWidth: 0,
});

const SegmentHeaderTitle = styled.div({
  fontSize: fontSize.s,
  fontWeight: fontWeight.heavy,
  lineHeight: 1.35,
});

const SegmentHeaderMeta = styled.div({
  fontSize: fontSize.xs,
  color: mediumGrey,
  marginTop: "2px",
  lineHeight: 1.45,
});

const PhaseLabelStart = styled.span({
  fontSize: fontSize.xs,
  fontWeight: fontWeight.bold,
  color: red,
  textTransform: "uppercase",
});

const TimelineRow = styled.div({
  display: "grid",
  gridTemplateColumns: "minmax(52px, 72px) 28px 1fr",
  gap: spacing.s,
  alignItems: "flex-start",
  marginBottom: spacing.m,
  position: "relative",
});

const TimeCol = styled.div({
  fontSize: fontSize.xs,
  fontWeight: fontWeight.bold,
  color: darkGrey,
  textAlign: "right",
  paddingTop: "10px",
});

const DotCol = styled.div({
  display: "flex",
  justifyContent: "center",
  paddingTop: "10px",
});

const PreRaceDotCol = styled.div({
  display: "flex",
  justifyContent: "center",
  paddingTop: "10px",
});

const Dot = styled.div({
  width: "14px",
  height: "14px",
  borderRadius: "50%",
  backgroundColor: green,
  flexShrink: 0,
});

const PreRaceDot = styled.div({
  width: "14px",
  height: "14px",
  borderRadius: "50%",
  backgroundColor: blue,
  flexShrink: 0,
});

const GoalDot = styled.div({
  width: "14px",
  height: "14px",
  borderRadius: "50%",
  backgroundColor: white,
  border: { xy: { style: "solid", color: red, width: "3px" } },
  flexShrink: 0,
});

const CardCol = styled.div({
  minWidth: 0,
});

const ProductRow = styled.div({
  display: "flex",
  flexDirection: "row",
  alignItems: "flex-start",
  gap: spacing.s,
});

const ProductThumbStrip = styled.div({
  display: "flex",
  flexDirection: "row",
  flexWrap: "wrap",
  alignItems: "flex-start",
  gap: spacing.m,
  marginBottom: spacing.s,
});

const ThumbCell = styled.div({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: spacing.xxs,
  width: "56px",
});

const ThumbLabel = styled.span({
  fontSize: "10px",
  fontWeight: fontWeight.bold,
  color: darkGrey,
  textAlign: "center",
  lineHeight: 1.2,
});

const CyclingBlockTitle = styled.div({
  fontSize: fontSize.s,
  fontWeight: fontWeight.bold,
  color: black,
  lineHeight: 1.35,
  marginBottom: spacing.xxs,
});

const Thumb = styled.img({
  width: "44px",
  height: "44px",
  objectFit: "contain",
  borderRadius: "4px",
  border: { xy: { style: "solid", color: grey, width: "1px" } },
  backgroundColor: white,
  flexShrink: 0,
});

const ThumbPlaceholder = styled.div({
  width: "44px",
  height: "44px",
  borderRadius: "4px",
  backgroundColor: mediumGrey,
  flexShrink: 0,
});

const ProductText = styled.div({
  flexGrow: 1,
  minWidth: 0,
});

const ProductTitle = styled.div({
  fontSize: fontSize.s,
  fontWeight: fontWeight.bold,
  color: black,
  lineHeight: 1.35,
});

const TagRow = styled.div({
  display: "flex",
  flexWrap: "wrap",
  gap: spacing.xxs,
  marginTop: spacing.xxs,
});

const tagBase = {
  display: "inline-block" as const,
  fontSize: "10px",
  fontWeight: fontWeight.bold,
  textTransform: "uppercase" as const,
  padding: { x: "6px", y: "2px" },
  borderRadius: "3px",
};

const CarbTag = styled.div({
  ...tagBase,
  backgroundColor: green,
  color: white,
});

const NitrateTag = styled.div({
  ...tagBase,
  backgroundColor: blue,
  color: white,
});

const CaffeineTag = styled.div({
  ...tagBase,
  backgroundColor: orange,
  color: black,
});

const NotePara = styled.p({
  fontSize: fontSize.xs,
  color: black,
  lineHeight: 1.5,
  margin: { top: 0, right: 0, bottom: 0, left: 0 },
});

const GoalLabel = styled.div({
  fontSize: fontSize.m,
  fontWeight: fontWeight.heavy,
  color: black,
  paddingTop: spacing.xxs,
});

const TimelineMuted = styled.p({
  fontSize: fontSize.xs,
  color: darkGrey,
  marginBottom: spacing.m,
});

const AfterGoal = styled.p({
  fontSize: fontSize.xs,
  color: black,
  lineHeight: 1.55,
  marginTop: spacing.l,
  paddingTop: spacing.m,
  borderTop: { style: "solid", color: grey, width: "1px" },
});
